import { useEffect, useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  PublisherActions,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { useAppDispatch } from '@wajeez-web/stores/base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { PlusIcon } from '@heroicons/react/solid';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

// import NarratorssModal from '../components/NarratorModal';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import PublisherModal from '../components/PublisherModal';

interface PublisherDataTableExtended extends Server.TableOfPublisherColumns {
  totalCount: number | undefined;
}

const PublishersPage = () => {
  const dispatch = useAppDispatch();

  const [isPublisherModalOpen, setIsPublisherModalOpen] = useState(false);
  const [selectedPublisherId, setSelectedPublisherId] = useState<
    number | undefined
  >(undefined);

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'publishers',
    PublisherActions.getPublishersDatatableAsync
  );

  const publishersDatatable = useContentSelector(
    (state) => state.publishers.datatable
  );

  const publishersDatatableData = publishersDatatable.data;

  const dataTableDataExtended =
    publishersDatatableData as PublisherDataTableExtended;

  const { t } = useTranslation();

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Publishers`}
        withoutRightPadding={false}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add New Publisher'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsPublisherModalOpen(true);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [AdminSystemPermissions.publishers.add],
          },
        ]}
        breadCrumbsLinks={[{ title: 'Publishers' }]}
      >
        <DataTableWrapper
          data={dataTableDataExtended as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={publishersDatatable.status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                permission: AdminSystemPermissions.publishers.edit,
                endpoint: '',
                type: 'request',
                action: () => {
                  setSelectedPublisherId(Number(row[1]));
                  setIsPublisherModalOpen(true);
                },
              },
            ];
          }}
          columnsSelectors={(r) => {
            return {
              '2': (
                <div
                  className="text-[#38A7B4] font-medium cursor-pointer"
                  onClick={() => {
                    setSelectedPublisherId(Number(r[1]));
                    setIsPublisherModalOpen(true);
                  }}
                >
                  {r[2]}
                </div>
              ),
            };
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      <PublisherModal
        isOpen={isPublisherModalOpen}
        onClose={() => {
          setIsPublisherModalOpen(false);
          setSelectedPublisherId(undefined);
        }}
        onSubmit={() => {
          getData();

          setIsPublisherModalOpen(false);
          setSelectedPublisherId(undefined);
        }}
        id={selectedPublisherId}
      />
    </div>
  );
};

export default PublishersPage;
